import { useEffect, useRef, useState } from "react"

import { useStripe, useElements, PaymentElement, ExpressCheckoutElement } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"

import styles from "./checkout_form.module.scss"

import { fetchAuthenticityToken } from "app/scripts/refresh_form_authenticity_token"
import PromoCodeForm from "app/components/subscriptions/promo_code_form"
import Tooltip from "shared/components/tooltip"
import { waitForCheckoutPageReady, checkoutPageReadyDiagnostics } from "./checkout_page_ready"
import { buildCheckoutRumContext } from "./checkout_rum_context"

import { normalizePaymentMethodType } from "app/utils/payment_method_type"
import { logCustomDatadogAction } from "app/scripts/shared_setup"
import { isTransientNetworkFetchError } from "shared/lib/xhr_promise"
import { captureException } from "@sentry/react"

// Stripe.js `confirmSetup` / `confirmPayment` reject with plain `StripeError` objects that don't extend Error.
// `new Error(String(error))` then yields "[object Object]" in Sentry, losing every actionable field.
// Wrap the error with a meaningful message and attach the Stripe-specific fields as `extra`.
const serializeStripeError = (error) => {
  if (error instanceof Error) {
    return { error, extras: undefined }
  }

  const isObject = error && typeof error === "object"
  const message = (isObject && (error.message || error.code)) || "Stripe error"
  const wrapped = new Error(message)
  if (isObject && error.code) {
    wrapped.name = `StripeError(${error.code})`
  }

  return {
    error: wrapped,
    extras: isObject
      ? {
        stripeError: {
          type: error.type,
          code: error.code,
          decline_code: error.decline_code,
          payment_method_type: error.payment_method?.type,
          request_log_url: error.request_log_url,
          message: error.message,
        },
      }
      : undefined,
  }
}

// Apple Pay / Google Pay: `auto` shows wallets when Stripe expects them to convert;
// `always` forces probe UI on more browsers (e.g. desktop Chromium Apple Pay on macOS).
const DEFAULT_EXPRESS_WALLET_PAYMENT_METHODS_MODE = "auto"
const EXPRESS_LINK_PAYMENT_METHODS_MODE = "auto"
const EXPRESS_LAYOUT_MAX_COLUMNS = 3
const EXPRESS_BUTTON_HEIGHT = 44
const EXPRESS_BUTTON_GAP = 8
const EXPRESS_ROW_HEIGHT_CSS_VAR = "--express-checkout-row-height"
const EXPRESS_ROW_MAX_MEASURED_HEIGHT = 55
// ab_express=always: overflow "never" requires maxRows 0 (Stripe); wallets may wrap to 2+ rows.
const EXPRESS_ROW_MAX_MEASURED_HEIGHT_ALWAYS =
  EXPRESS_BUTTON_HEIGHT * 3 + EXPRESS_BUTTON_GAP * 2
const EXPRESS_ROW_MIN_RESERVE_ALWAYS = EXPRESS_BUTTON_HEIGHT * 2 + EXPRESS_BUTTON_GAP
const EXPRESS_CHECKOUT_LAYOUT_DEFAULT = {
  maxColumns: EXPRESS_LAYOUT_MAX_COLUMNS,
  maxRows: 1,
  overflow: "auto",
}
const EXPRESS_CHECKOUT_LAYOUT_ALWAYS_WALLETS = {
  maxColumns: EXPRESS_LAYOUT_MAX_COLUMNS,
  maxRows: 0,
  overflow: "never",
}

// Stripe wallet probe: null = in progress, true = ≥1 method, false = none.
const WALLET_PROBE_PENDING = null
const WALLET_PROBE_HAS_WALLETS = true
const WALLET_PROBE_NO_WALLETS = false

const isWalletProbePending = availability => availability === WALLET_PROBE_PENDING
const isWalletProbeSettled = availability => availability !== WALLET_PROBE_PENDING
const hasExpressWalletsAvailable = availability => availability === WALLET_PROBE_HAS_WALLETS
const shouldReserveExpressCheckoutChrome = availability =>
  availability !== WALLET_PROBE_NO_WALLETS

const hasExpressWallets = availablePaymentMethods => Boolean(
  availablePaymentMethods?.applePay
  || availablePaymentMethods?.googlePay
  || availablePaymentMethods?.link
)

const isChromeOnIos = () => /CriOS/i.test(navigator.userAgent || "")

const resolveExpressWalletPaymentMethodsForMount = (mode) => {
  if (mode !== "always") {
    return { effectiveMode: mode, downgraded: false, downgradeReason: null }
  }
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return { effectiveMode: "auto", downgraded: true, downgradeReason: "insecure_context" }
  }
  if (isChromeOnIos()) {
    return { effectiveMode: "auto", downgraded: true, downgradeReason: "chrome_ios" }
  }
  return { effectiveMode: "always", downgraded: false, downgradeReason: null }
}

// Backoff between create_intent retries (ms) — gives flaky mobile networks time to recover.
const SETUP_INTENT_RETRY_BACKOFF_MS = [250, 500, 1000]

const isDestroyedStripeElementError = (error) => {
  const message = String(error?.message || "")
  return message.includes("already been destroyed")
}

const captureCheckoutException = (error, context) => {
  if (isDestroyedStripeElementError(error)) return
  captureException(error, context)
}

const PAYMENT_ELEMENT_LAYOUT_OPTIONS = {
  tabs: { type: "tabs" },
  accordion_radios: {
    type: "accordion",
    defaultCollapsed: false,
    radios: "always",
  },
  accordion_no_radios: {
    type: "accordion",
    defaultCollapsed: false,
    radios: "never",
    spacedAccordionItems: false,
  },
}

// Derives express-checkout chrome flags in one place (wallet probe → row reserve → reveal).
// ResizeObserver + rAF only run after wallets settle; Datadog `express_checkout_ready` tracks outcomes.
const resolveExpressCheckoutDisplay = ({
  stripeReady,
  walletsAvailable,
  walletProbeTimedOut,
  expressRowHeightLocked,
  expressCheckoutPainted,
  paymentElementReady,
}) => {
  const reserveChrome = shouldReserveExpressCheckoutChrome(walletsAvailable)
  const rowLayoutReady =
    !hasExpressWalletsAvailable(walletsAvailable) || expressRowHeightLocked
  const showSkeletonOverlay =
    reserveChrome
    && !walletProbeTimedOut
    && (
      !stripeReady
      || !rowLayoutReady
      || !expressCheckoutPainted
      || !paymentElementReady
    )
  const walletsResolved =
    paymentElementReady
    && (isWalletProbeSettled(walletsAvailable) || walletProbeTimedOut)

  return {
    reserveChrome,
    showSkeletonOverlay,
    walletsResolved,
    isProbing: !stripeReady || isWalletProbePending(walletsAvailable),
  }
}

const CheckoutForm = ({
  buttonConfig,
  checkoutAbVariants,
  email,
  expressCheckoutPlacement = "above",
  expressWalletPaymentMethodsMode = DEFAULT_EXPRESS_WALLET_PAYMENT_METHODS_MODE,
  initialClientSecret = null,
  initialReturnUrl = null,
  isBillingUpdateFlow,
  onCheckoutFormInteractive,
  onCreateSubscriptionStart,
  onCreateSubscriptionCancel,
  onPaymentMethodSelect,
  onSetupIntentError,
  paymentElementLayout = "tabs",
  processedAfterPaymentMethodSetup,
  promoCodeConfig,
  restrictToCardOnly = false,
  subscriptionSuccessPath,
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [clientSecret, setClientSecret] = useState(initialClientSecret)
  const [returnUrl, setReturnUrl] = useState(initialReturnUrl)
  const [intentError, setIntentError] = useState(null)
  const [intentSessionExpired, setIntentSessionExpired] = useState(false)
  const [isIntentLoading, setIsIntentLoading] = useState(!initialClientSecret)
  const [handlingSubmit, setHandlingSubmit] = useState(false)
  const [pageReady, setPageReady] = useState(false)
  const [walletsAvailable, setWalletsAvailable] = useState(WALLET_PROBE_PENDING)
  const [walletProbeTimedOut, setWalletProbeTimedOut] = useState(false)
  const [paymentElementReady, setPaymentElementReady] = useState(false)
  const [expressCheckoutPainted, setExpressCheckoutPainted] = useState(false)
  const expressWalletsAlwaysMode = expressWalletPaymentMethodsMode === "always"
  const [expressRowHeightPx, setExpressRowHeightPx] = useState(() =>
    expressWalletsAlwaysMode ? EXPRESS_ROW_MIN_RESERVE_ALWAYS : EXPRESS_BUTTON_HEIGHT
  )
  const [expressRowHeightLocked, setExpressRowHeightLocked] = useState(false)
  const expressMountRef = useRef(null)
  const stripeLoadStartRef = useRef(null)
  const lastTrackedSelectionRef = useRef(null)
  const isMountedRef = useRef(true)
  const setupIntentSourceRef = useRef(initialClientSecret ? "embedded" : "fetch")
  const expressCheckoutReadyLoggedRef = useRef(false)
  const expressWalletDowngradeLoggedRef = useRef(false)
  const checkoutFormInteractiveTrackedRef = useRef(false)

  const expressWalletConfig = resolveExpressWalletPaymentMethodsForMount(expressWalletPaymentMethodsMode)
  const effectiveExpressWalletMode = expressWalletConfig.effectiveMode

  const checkoutRumContext = (overrides = {}) => buildCheckoutRumContext({
    paymentElementLayout,
    expressCheckoutPlacement,
    expressWalletPaymentMethodsMode,
    restrictToCardOnly,
    setupIntentSource: setupIntentSourceRef.current,
    checkoutAbVariants,
    ...overrides,
  })

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const initSetupIntent = async () => {
      const pageReadyResult = await waitForCheckoutPageReady()
      if (cancelled) return

      const pageDiagnostics = checkoutPageReadyDiagnostics()
      logCustomDatadogAction("checkout_page_ready_wait", {
        ...checkoutRumContext(),
        wait_time_ms: pageReadyResult.waitTimeMs,
        anti_flicker_cleared: pageReadyResult.antiFlickerCleared,
        max_wait_ms: pageReadyResult.maxWaitMs,
        ...pageDiagnostics,
      })

      if (!pageReadyResult.antiFlickerCleared) {
        logCustomDatadogAction("checkout_page_ready_timeout", {
          ...checkoutRumContext(),
          wait_time_ms: pageReadyResult.waitTimeMs,
          max_wait_ms: pageReadyResult.maxWaitMs,
          ...pageDiagnostics,
        })
      }

      setPageReady(true)

      if (initialClientSecret && initialReturnUrl) {
        setClientSecret(initialClientSecret)
        setReturnUrl(initialReturnUrl)
        setIsIntentLoading(false)
        logCustomDatadogAction("checkout_setup_intent_embedded", checkoutRumContext())
        return
      }

      fetchCreateIntent()
    }

    initSetupIntent()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!expressWalletConfig.downgraded || expressWalletDowngradeLoggedRef.current) {
      return undefined
    }

    expressWalletDowngradeLoggedRef.current = true
    logCustomDatadogAction("express_wallet_mode_downgraded", {
      ...checkoutRumContext(),
      downgrade_reason: expressWalletConfig.downgradeReason,
      requested_mode: expressWalletPaymentMethodsMode,
      effective_mode: effectiveExpressWalletMode,
    })
  }, [
    expressWalletConfig.downgraded,
    expressWalletConfig.downgradeReason,
    expressWalletPaymentMethodsMode,
    effectiveExpressWalletMode,
  ])

  // With paymentMethods "auto", onReady may still omit availablePaymentMethods briefly while Stripe probes.
  // Do not force-hide the express row on timeout; only stop the skeleton.
  useEffect(() => {
    if (!stripe || !elements || isWalletProbeSettled(walletsAvailable) || walletProbeTimedOut) {
      return undefined
    }

    const probeTimeoutMs = 5000
    const timeoutId = window.setTimeout(() => {
      setWalletProbeTimedOut(true)
    }, probeTimeoutMs)

    return () => window.clearTimeout(timeoutId)
  }, [stripe, elements, walletsAvailable, walletProbeTimedOut])

  // Keep the skeleton overlay until Stripe has had two frames to paint wallet buttons (avoids swap flicker).
  useEffect(() => {
    if (!hasExpressWalletsAvailable(walletsAvailable)) {
      setExpressCheckoutPainted(false)
      return undefined
    }

    let cancelled = false
    let frame2Id

    const frame1Id = requestAnimationFrame(() => {
      frame2Id = requestAnimationFrame(() => {
        if (!cancelled) setExpressCheckoutPainted(true)
      })
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(frame1Id)
      if (frame2Id !== undefined) cancelAnimationFrame(frame2Id)
    }
  }, [walletsAvailable])

  const toSetupIntentErrorMessage = error => {
    if (isTransientNetworkFetchError(error)) {
      return "Connection issue. Check your network and tap Retry loading payment form."
    }
    const statusHint = String(error?.message || "")
    if (statusHint.includes("429")) return "Too many payment requests right now. Please wait a moment and try again."
    return "We couldn't load the secure payment form. Please retry."
  }

  const postCreateIntent = async (path, { mustBeFreshToken = false } = {}) => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), 8000)
    try {
      const headers = {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": await fetchAuthenticityToken(mustBeFreshToken),
      }

      return await fetch(path, {
        method: "POST",
        signal: controller.signal,
        headers,
      })
    } finally {
      window.clearTimeout(timeoutId)
    }
  }

  const fetchCreateIntent = async (maxAttempts = 3, { mustBeFreshToken = false } = {}) => {
    setupIntentSourceRef.current = "fetch"
    setClientSecret(null)
    if (!isMountedRef.current) return
    setIsIntentLoading(true)
    setIntentError(null)
    setIntentSessionExpired(false)

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const createIntentPath = "/subscriptions/create_intent"
        let res = await postCreateIntent(createIntentPath, { mustBeFreshToken })
        if (!isMountedRef.current) return

        if (res.status === 404) {
          res = await postCreateIntent("/subscriptions/create_intent.json", { mustBeFreshToken })
          if (!isMountedRef.current) return
        }

        // Session expired (cookie/CSRF no longer valid) — retrying 3× will not recover and
        // the generic "couldn't load" message hides the real cause from users. Short-circuit
        // with an explicit "refresh and sign in" CTA and skip the Sentry capture (expected).
        if (res.status === 401) {
          const message = "Your session has expired. Please refresh the page and sign in again."
          setClientSecret(null)
          if (!isMountedRef.current) return
          setIntentError(message)
          setIntentSessionExpired(true)
          setIsIntentLoading(false)
          onSetupIntentError?.(message)
          return
        }

        if (!res.ok) {
          logCustomDatadogAction("checkout_intent_fetch_failed", {
            ...checkoutRumContext(),
            ...checkoutPageReadyDiagnostics(),
            status: res.status,
            path: res.url || createIntentPath,
            attempt,
          })
          const errorResponse = await res.json().catch(() => ({}))
          if (!isMountedRef.current) return
          const apiErrorMessage = errorResponse?.error?.message
          throw new Error(apiErrorMessage || `HTTP ${res.status}`)
        }

        const result = await res.json()
        if (!isMountedRef.current) return
        setClientSecret(result.clientSecret)
        setReturnUrl(result.returnUrl)
        setIsIntentLoading(false)
        return
      } catch (error) {
        if (attempt < maxAttempts) {
          const backoffMs = SETUP_INTENT_RETRY_BACKOFF_MS[attempt - 1] ?? 2000
          await new Promise(resolve => setTimeout(resolve, backoffMs))
          if (!isMountedRef.current) return
        } else {
          const message = toSetupIntentErrorMessage(error)
          if (isTransientNetworkFetchError(error)) {
            logCustomDatadogAction("checkout_intent_network_error", {
              ...checkoutRumContext(),
              ...checkoutPageReadyDiagnostics(),
              attempt,
              error_name: error?.name,
            })
          } else {
            const { error: serializedError, extras } = serializeStripeError(error)
            captureCheckoutException(serializedError, {
              tags: { product_area: "subscriptions", checkout: "setup_intent" },
              ...(extras ? { extra: extras } : {}),
            })
          }
          if (!isMountedRef.current) return
          setIntentError(message)
          setIsIntentLoading(false)
          onSetupIntentError?.(message)
        }
      }
    }
  }

  const openZendesk = (e) => {
    e.preventDefault()
    window.App.loadZendeskMessenger()
  }

  const SETUP_INTENT_SECRET_PATTERN = /^seti_.*secret_/
  const PAYMENT_INTENT_SECRET_PATTERN = /^pi_.*secret_/

  // Stripe's deferred-intent flow requires elements.submit() immediately before every
  // confirm call. The caller submits before the first attempt; on a lock_timeout retry we
  // must re-submit, otherwise the retried confirm is rejected with "elements.submit() must
  // be called before stripe.confirm…()".
  const resubmitElements = async () => {
    const { error } = await elements.submit()
    return error
  }

  const confirmSetupWithRetry = async (options, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      if (i > 0) {
        const resubmitError = await resubmitElements()
        if (resubmitError) return { error: resubmitError }
      }
      const result = await stripe.confirmSetup(options)
      if (result.error?.code !== "lock_timeout" || i === maxRetries - 1) return result
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)))
    }
  }

  const confirmPaymentWithRetry = async (options, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      if (i > 0) {
        const resubmitError = await resubmitElements()
        if (resubmitError) return { error: resubmitError }
      }
      const result = await stripe.confirmPayment(options)
      if (result.error?.code !== "lock_timeout" || i === maxRetries - 1) return result
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)))
    }
  }

  const handleNextActionWithRetry = async (clientSecret, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      const result = await stripe.handleNextAction({ clientSecret })
      if (result.error?.code !== "lock_timeout" || i === maxRetries - 1) return result
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)))
    }
  }

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    if (!paymentFormReady) {
      const message = "Payment form is still loading. Please retry in a moment."
      window.appendFlash(message, "danger")
      setHandlingSubmit(false)
      return
    }

    if (!stripe || !elements || !clientSecret || !returnUrl) {
      const message = "Payment form is still loading. Please retry in a moment."
      window.appendFlash(message, "danger")
      setHandlingSubmit(false)
      return
    }

    try {
      const submitResult = await elements.submit()
      if (!isMountedRef.current) return
      if (submitResult.error) {
        window.appendFlash(submitResult.error.message, "danger")
        setHandlingSubmit(false)
        return
      }

      if (!submitResult.selectedPaymentMethod) {
        onCreateSubscriptionCancel({ message: "Please choose a payment method and try again." })
        setHandlingSubmit(false)
        return
      }

      await completeSetupIntent(submitResult.selectedPaymentMethod)
      if (!isMountedRef.current) return
    } catch (error) {
      if (!isMountedRef.current) return
      const { error: serializedError, extras } = serializeStripeError(error)
      captureCheckoutException(serializedError, {
        tags: { product_area: "subscriptions", checkout: "checkout_submit" },
        ...(extras ? { extra: extras } : {}),
      })
      onCreateSubscriptionCancel(error)
      setHandlingSubmit(false)
    }
  }

  const completeSetupIntent = async (selectedPaymentMethod, options = {}) => {
    if (!isMountedRef.current) return
    const onPaymentFailure = options.onPaymentFailure
    const paymentMethodType = normalizePaymentMethodType(selectedPaymentMethod)

    onCreateSubscriptionStart?.(paymentMethodType)

    const confirmParams = {
      return_url: returnUrl,
      payment_method_data: {
        billing_details: {
          address: {
            city: null,
            country: null,
            line1: null,
            line2: null,
            postal_code: null,
            state: null
          },
          email: email,
        },
      },
    }

    const confirmationResult = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams,
      redirect: "if_required"
    })
    if (!isMountedRef.current) return

    if (confirmationResult.error) {
      onCreateSubscriptionCancel(confirmationResult.error)
      onPaymentFailure?.(confirmationResult.error.message || "Payment failed. Please try again.")
      setHandlingSubmit(false)
      return
    }

    const setupIntentPaymentMethod = confirmationResult.setupIntent?.payment_method
    const paymentMethodId = typeof setupIntentPaymentMethod === "string"
      ? setupIntentPaymentMethod
      : setupIntentPaymentMethod?.id

    if (!paymentMethodId) {
      const verificationError = {
        message: "We couldn't verify your payment method. Please try again."
      }
      onCreateSubscriptionCancel(verificationError)
      onPaymentFailure?.(verificationError.message)
      setHandlingSubmit(false)
      return
    }

    const requiresAction = async (intentClientSecret, subscriptionId) => {
      // Billing update on a past_due subscription: the invoice's PaymentIntent is already
      // confirmed server-side (payment method attached, status `requires_action`), so we only
      // need to finish 3DS. confirmPayment would re-send card data and force the Elements
      // `setup_future_usage` (off_session) onto an invoice PI created with `null`, which Stripe
      // rejects. handleNextAction takes only the client secret, sidestepping both issues.
      if (isBillingUpdateFlow && intentClientSecret.match(PAYMENT_INTENT_SECRET_PATTERN)) {
        const { error } = await handleNextActionWithRetry(intentClientSecret)
        if (error) throw error
        return
      }

      // Stripe requires elements.submit() before each confirmPayment call, even after the
      // initial confirmSetup — the clientSecret changed so the Elements state must be re-validated.
      const {error: resubmitError} = await elements.submit()
      if (resubmitError) throw resubmitError

      const successConfirmParams = subscriptionSuccessPath
        ? { ...confirmParams, return_url: `${subscriptionSuccessPath.replace(/\/$/, "")}/${subscriptionId}` }
        : confirmParams

      if (intentClientSecret.match(SETUP_INTENT_SECRET_PATTERN)) {
        const { error, setupIntent } = await confirmSetupWithRetry({
          elements,
          clientSecret: intentClientSecret,
          confirmParams: successConfirmParams,
          redirect: "if_required",
        })

        if (error) {
          // User is already trialing and will receive notification again from Stripe to confirm the payment before trial finishes.
          // We'll throw error just to track but we'll not stop user.
          throw error
        } else if (setupIntent.status === "succeeded") {
          fetch("/subscriptions/update_customer_default_payment_method.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": await fetchAuthenticityToken(),
            },
            body: JSON.stringify({
              payment_method: setupIntent.payment_method,
            }),
          })
        }
      } else if (intentClientSecret.match(PAYMENT_INTENT_SECRET_PATTERN)) {
        const {error} = await confirmPaymentWithRetry({
          elements,
          clientSecret: intentClientSecret,
          confirmParams: successConfirmParams,
          redirect: "if_required"
        })

        if (error) throw error
      } else {
        throw new Error("Invalid client secret format")
      }
    }

    processedAfterPaymentMethodSetup(paymentMethodId, requiresAction, () => {
      setHandlingSubmit(false)
      fetchCreateIntent()
    }, paymentMethodType)
  }

  const trackSelection = rawPaymentMethodType => {
    const paymentMethodType = normalizePaymentMethodType(rawPaymentMethodType)
    if (lastTrackedSelectionRef.current === paymentMethodType) return
    lastTrackedSelectionRef.current = paymentMethodType
    onPaymentMethodSelect?.(paymentMethodType)
  }

  const onPaymentElementChange = event => {
    trackSelection(event?.value?.type)
  }

  const onExpressCheckoutConfirm = async event => {
    if (handlingSubmit) return
    setHandlingSubmit(true)

    if (!checkoutFormInteractive || !stripe || !elements || !clientSecret || !returnUrl) {
      const message = "Payment form is still loading. Please retry in a moment."
      window.appendFlash(message, "danger")
      event?.paymentFailed?.({ message })
      setHandlingSubmit(false)
      return
    }

    try {
      const submitResult = await elements.submit()
      if (!isMountedRef.current) return
      if (submitResult.error) {
        const message = submitResult.error.message || "Payment failed. Please try again."
        logCustomDatadogAction("express_checkout_submit_validation_failed", {
          error_code: submitResult.error.code,
          error_type: submitResult.error.type,
          express_payment_type: event?.expressPaymentType,
        })
        event?.paymentFailed?.({ message })
        window.appendFlash(message, "danger")
        setHandlingSubmit(false)
        return
      }

      const paymentMethodType = normalizePaymentMethodType(event?.expressPaymentType)
      trackSelection(paymentMethodType)
      await completeSetupIntent(paymentMethodType, {
        onPaymentFailure: message => {
          event?.paymentFailed?.({ message })
        },
      })
      if (!isMountedRef.current) return
    } catch (error) {
      if (!isMountedRef.current) return
      const { error: serializedError, extras } = serializeStripeError(error)
      captureCheckoutException(serializedError, {
        tags: { product_area: "subscriptions", checkout: "express_checkout_submit" },
        ...(extras ? { extra: extras } : {}),
      })
      const message = error?.message || "Payment failed. Please try again."
      event?.paymentFailed?.({ message })
      onCreateSubscriptionCancel(error)
      setHandlingSubmit(false)
    }
  }

  const logExpressWalletAvailability = (availablePaymentMethods, source) => {
    const linkOnly =
      Boolean(availablePaymentMethods?.link)
      && !availablePaymentMethods?.applePay
      && !availablePaymentMethods?.googlePay

    if (source === "ready") {
      expressCheckoutReadyLoggedRef.current = true
    }

    logCustomDatadogAction(
      source === "ready" ? "express_checkout_ready" : "express_checkout_payment_methods_change",
      {
        apple_pay_mode: effectiveExpressWalletMode,
        google_pay_mode: effectiveExpressWalletMode,
        link_mode: EXPRESS_LINK_PAYMENT_METHODS_MODE,
        source,
        has_available_payment_methods: availablePaymentMethods !== undefined,
        apple_pay_available: Boolean(availablePaymentMethods?.applePay),
        google_pay_available: Boolean(availablePaymentMethods?.googlePay),
        link_available: Boolean(availablePaymentMethods?.link),
        link_only: linkOnly,
      }
    )
  }

  const applyAvailablePaymentMethods = (availablePaymentMethods, source) => {
    logExpressWalletAvailability(availablePaymentMethods, source)

    if (availablePaymentMethods === undefined) {
      // onReady can fire before the wallet probe finishes — wait for change event.
      if (source === "ready") return
      setWalletsAvailable(WALLET_PROBE_NO_WALLETS)
      return
    }

    setWalletsAvailable(hasExpressWallets(availablePaymentMethods))
  }

  const onExpressCheckoutReady = event => {
    if (!isMountedRef.current) return
    applyAvailablePaymentMethods(event?.availablePaymentMethods, "ready")
  }

  const onExpressCheckoutAvailablePaymentMethodsChange = event => {
    if (!isMountedRef.current) return
    const availablePaymentMethods =
      event?.availablePaymentMethods ?? event?.paymentMethods
    applyAvailablePaymentMethods(availablePaymentMethods, "change")
  }

  const onExpressCheckoutLoadError = () => {
    if (!isMountedRef.current) return
    setWalletsAvailable(WALLET_PROBE_NO_WALLETS)
    logCustomDatadogAction("express_checkout_load_error", checkoutRumContext())
  }

  const autoFocus = element => {
    if (!window.App.integrationTestMode) element.focus()
  }

  const loaderStartTrack = () => {
    stripeLoadStartRef.current = performance.now()
  }

  const readyTrack = (element) => {
    const stripeLoadTime = performance.now() - stripeLoadStartRef.current

    autoFocus(element)
    setPaymentElementReady(true)

    logCustomDatadogAction("stripe_load_completed", {
      ...checkoutRumContext(),
      loadTime: stripeLoadTime,
    })
  }

  const stripeReady = Boolean(stripe && elements)

  const expressRowMaxMeasuredHeight = expressWalletsAlwaysMode
    ? EXPRESS_ROW_MAX_MEASURED_HEIGHT_ALWAYS
    : EXPRESS_ROW_MAX_MEASURED_HEIGHT

  // Match skeleton reserve to Stripe’s rendered row (iframes often report ~48px including wrapper chrome).
  useEffect(() => {
    if (!stripeReady || !hasExpressWalletsAvailable(walletsAvailable)) {
      setExpressRowHeightPx(EXPRESS_BUTTON_HEIGHT)
      setExpressRowHeightLocked(false)
      return undefined
    }

    const mountEl = expressMountRef.current
    if (!mountEl) return undefined

    const syncRowHeight = () => {
      const boundsHeight = Math.ceil(mountEl.getBoundingClientRect().height)
      const contentHeight = Math.ceil(mountEl.scrollHeight)
      const measured = expressWalletsAlwaysMode
        ? Math.max(boundsHeight, contentHeight)
        : boundsHeight
      if (measured >= EXPRESS_BUTTON_HEIGHT && measured <= expressRowMaxMeasuredHeight) {
        setExpressRowHeightPx(measured)
        setExpressRowHeightLocked(true)
      }
    }

    syncRowHeight()
    const observer = new ResizeObserver(syncRowHeight)
    observer.observe(mountEl)

    return () => observer.disconnect()
  }, [stripeReady, walletsAvailable, expressRowMaxMeasuredHeight, expressWalletsAlwaysMode])

  const paymentFormReady =
    pageReady
    && stripeReady
    && Boolean(clientSecret)
    && !isIntentLoading
    && !intentError
    && paymentElementReady

  const expressCheckoutDisplay = resolveExpressCheckoutDisplay({
    stripeReady,
    walletsAvailable,
    walletProbeTimedOut,
    expressRowHeightLocked,
    expressCheckoutPainted,
    paymentElementReady,
  })

  const checkoutFormInteractive =
    pageReady
    && paymentFormReady
    && expressCheckoutDisplay.walletsResolved
  const showCheckoutTooltip = checkoutFormInteractive && !handlingSubmit

  const shouldMountExpressCheckout =
    pageReady
    && stripeReady
    && Boolean(clientSecret)
    && !isIntentLoading

  useEffect(() => {
    if (!shouldMountExpressCheckout) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      if (isWalletProbePending(walletsAvailable) && !expressCheckoutReadyLoggedRef.current) {
        logCustomDatadogAction("express_checkout_timeout", {
          ...checkoutRumContext(),
          ...checkoutPageReadyDiagnostics(),
        })
      }
    }, 5000)

    return () => window.clearTimeout(timeoutId)
  }, [shouldMountExpressCheckout, walletsAvailable])

  useEffect(() => {
    if (checkoutFormInteractiveTrackedRef.current || !checkoutFormInteractive) {
      return undefined
    }

    checkoutFormInteractiveTrackedRef.current = true
    logCustomDatadogAction("checkout_form_interactive", {
      hadInitialClientSecret: Boolean(initialClientSecret),
    })
    onCheckoutFormInteractive?.()
  }, [checkoutFormInteractive, initialClientSecret, onCheckoutFormInteractive])

  const expressRowStyle = {
    [EXPRESS_ROW_HEIGHT_CSS_VAR]: `${expressRowHeightPx}px`,
  }
  const walletsBelowCta = expressCheckoutPlacement === "below_cta"
  const expressCheckoutLayout = expressWalletsAlwaysMode
    ? EXPRESS_CHECKOUT_LAYOUT_ALWAYS_WALLETS
    : EXPRESS_CHECKOUT_LAYOUT_DEFAULT
  const paymentLayoutOptions =
    PAYMENT_ELEMENT_LAYOUT_OPTIONS[paymentElementLayout] || PAYMENT_ELEMENT_LAYOUT_OPTIONS.tabs

  const expressCheckoutSlot = (
    <div
      className={`${styles["express-checkout-slot"]} ${expressWalletsAlwaysMode ? styles["express-checkout-slot--always-wallets"] : ""}`}
      data-testid="express-checkout-slot"
      data-wallets-probing={expressCheckoutDisplay.isProbing ? "true" : "false"}
      style={expressRowStyle}
    >
      <div
        className={`${styles["express-checkout-inner"]} ${expressCheckoutDisplay.showSkeletonOverlay ? styles["express-checkout-inner--loading"] : ""}`}
      >
        {shouldMountExpressCheckout && (
          <div className={styles["express-checkout-mount"]} ref={expressMountRef}>
            <ExpressCheckoutElement
              onReady={onExpressCheckoutReady}
              onAvailablePaymentMethodsChange={onExpressCheckoutAvailablePaymentMethodsChange}
              onLoadError={onExpressCheckoutLoadError}
              onConfirm={onExpressCheckoutConfirm}
              options={{
                buttonType: {
                  // Stripe Express Checkout has no "pay" type; use plain for billing update.
                  applePay: isBillingUpdateFlow ? "plain" : "subscribe",
                  googlePay: isBillingUpdateFlow ? "plain" : "subscribe",
                },
                buttonTheme: {
                  applePay: "black",
                },
                buttonHeight: EXPRESS_BUTTON_HEIGHT,
                layout: expressCheckoutLayout,
                paymentMethodOrder: ["applePay", "googlePay", "link"],
                paymentMethods: {
                  applePay: effectiveExpressWalletMode,
                  googlePay: effectiveExpressWalletMode,
                  link: EXPRESS_LINK_PAYMENT_METHODS_MODE,
                  amazonPay: "never",
                  paypal: "never",
                  klarna: "never",
                },
                billingAddressRequired: false,
                emailRequired: false,
                shippingAddressRequired: false,
              }}
            />
          </div>
        )}
        <div
          className={styles["express-checkout-skeleton-overlay"]}
          aria-hidden={!expressCheckoutDisplay.showSkeletonOverlay}
        >
          <div className={styles["express-checkout-skeleton-bar"]} />
        </div>
      </div>
    </div>
  )

  const expressCheckoutDivider = (
    <div className="or-divider" aria-hidden data-testid="express-checkout-divider">
      <span>or</span>
    </div>
  )

  const expressCheckoutRegion = expressCheckoutDisplay.reserveChrome && (
    <div className={styles["express-checkout-region"]} data-testid="express-checkout-region">
      {!walletsBelowCta && expressCheckoutSlot}
      {!walletsBelowCta && expressCheckoutDivider}
      {walletsBelowCta && expressCheckoutDivider}
      {walletsBelowCta && expressCheckoutSlot}
    </div>
  )

  const fineprintBlock = (
    <p className="cta-fineprint">
      {buttonConfig.showTrialFineprint && (
        <>
          No charge for 7 days. Cancel anytime.
          <br />
        </>
      )}
      Need help?{" "}
      <a href="#" className="fineprint-link" onClick={openZendesk}>
        Talk to us
      </a>
    </p>
  )

  return (
    <form
      className={`form payment-info-form ${styles["payment-info-form"]} ${isBillingUpdateFlow ? styles["payment-info-form--billing-update"] : ""}`}
      data-testid="checkout-form"
      data-wallets-resolved={expressCheckoutDisplay.walletsResolved ? "true" : "false"}
      data-express-checkout-placement={expressCheckoutPlacement}
      data-express-wallet-mode={expressWalletPaymentMethodsMode}
      data-express-wallet-effective-mode={effectiveExpressWalletMode}
      data-page-ready={pageReady ? "true" : "false"}
      data-payment-element-layout={paymentElementLayout}
      data-restrict-to-card-only={restrictToCardOnly ? "true" : "false"}
      onSubmit={handleSubmit}
    >
      <div className={styles["checkout-form-layout"]}>
        {expressCheckoutRegion}

        <div className={styles["payment-element-slot"]}>
        {pageReady && stripeReady ? (
          <div className={`${styles["stripe-mount"]} credit-card-field`}>
            <PaymentElement
              onChange={onPaymentElementChange}
              onLoaderStart={loaderStartTrack}
              onReady={readyTrack}
              options={{
                fields: {
                  billingDetails: {
                    address: "never",
                    email: "never",
                  },
                },
                layout: paymentLayoutOptions,
                defaultValues: {
                  billingDetails: {
                    email: email,
                  },
                },
                terms: {
                  card: "never"
                },
                wallets: {
                  applePay: "never",
                  googlePay: "never",
                  link: "never",
                }
              }}
            />

            {promoCodeConfig?.initialPromoCode && (<PromoCodeForm {...promoCodeConfig} />)}
          </div>
        ) : (
          <div
            className={styles["payment-element-placeholder"]}
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading payment form"
          >
            <div className={styles["skeleton-field"]} aria-hidden />
            <div className={styles["skeleton-split-row"]} aria-hidden>
              <div className={styles["skeleton-field-half"]} />
              <div className={styles["skeleton-field-half"]} />
            </div>
            <p className={styles["status-copy"]}>
              Loading secure payment form...
            </p>
          </div>
        )}
        </div>

        {intentError && (
        <div className={styles["intent-error"]} aria-live="polite">
          <p>{intentError}</p>
          {intentSessionExpired ? (
            <button
              type="button"
              className="link-inline"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          ) : (
            <button
              type="button"
              className="link-inline"
              onClick={() => fetchCreateIntent(3, { mustBeFreshToken: true })}
            >
              Retry loading payment form
            </button>
          )}
        </div>
        )}

        <section className={styles["action-buttons"]}>
          <button
            id="submit-payment"
            type="submit"
            className={buttonConfig.buttonType ? "button-secondary full-width" : "button-primary full-width"}
            title={buttonConfig.cta}
            aria-label={buttonConfig.cta}
            data-dd-action-name="Subscription credit card payment button"
            disabled={!paymentFormReady || handlingSubmit}
          >
            {handlingSubmit ? buttonConfig.submittingCopy : buttonConfig.cta}
            {showCheckoutTooltip && (
              <Tooltip>
                Please be sure you’ve correctly completed the form. <br />
                <a className="link-inline" onClick={openZendesk}>
                  Contact us
                </a>
                for help.
              </Tooltip>
            )}
          </button>
          {!walletsBelowCta && fineprintBlock}
        </section>

        {walletsBelowCta && (
          <section className={`${styles["action-buttons"]} ${styles["action-buttons--fineprint-only"]}`}>
            {fineprintBlock}
          </section>
        )}
      </div>
    </form>
  )
}

CheckoutForm.propTypes = {
  buttonConfig: PropTypes.shape({
    buttonType: PropTypes.string,
    cta: PropTypes.string.isRequired,
    submittingCopy: PropTypes.string.isRequired,
    showTrialFineprint: PropTypes.bool,
  }),
  checkoutAbVariants: PropTypes.shape({
    wallet: PropTypes.oneOf(["below"]),
    payment: PropTypes.oneOf(["card_only"]),
    layout: PropTypes.oneOf(["accordion_radios", "accordion_no_radios"]),
    pricing: PropTypes.oneOf(["annual_only"]),
    express: PropTypes.oneOf(["always"]),
  }),
  email: PropTypes.string.isRequired,
  expressCheckoutPlacement: PropTypes.oneOf(["above", "below_cta"]),
  expressWalletPaymentMethodsMode: PropTypes.oneOf(["auto", "always"]),
  initialClientSecret: PropTypes.string,
  initialReturnUrl: PropTypes.string,
  isBillingUpdateFlow: PropTypes.bool,
  onCheckoutFormInteractive: PropTypes.func,
  onCreateSubscriptionCancel: PropTypes.func,
  onCreateSubscriptionStart: PropTypes.func,
  onPaymentMethodSelect: PropTypes.func,
  onSetupIntentError: PropTypes.func,
  paymentElementLayout: PropTypes.oneOf(["tabs", "accordion_radios", "accordion_no_radios"]),
  processedAfterPaymentMethodSetup: PropTypes.func.isRequired,
  promoCodeConfig: PropTypes.shape(),
  restrictToCardOnly: PropTypes.bool,
  subscriptionSuccessPath: PropTypes.string,
}

export default CheckoutForm
